import { Component, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DatePickerDayData {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isWeekend: boolean;
  isInRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
}

@Component({
  selector: 'umss-date-picker-day',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './date-picker-day.component.html',
  styleUrl: './date-picker-day.component.scss'
})
export class UmssDatePickerDayComponent {
  @HostBinding("style.display") display = "contents";

  constructor() { }

  /** Content props */
  @Input() dayData!: DatePickerDayData;
  @Input() locale: string = 'en-US';

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant: 'Default' | 'Compact' | 'Range' = 'Default';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;

  /** Range selection props */
  @Input() allowRangeSelection: boolean = false;
  @Input() isHovered: boolean = false;

  /** Event outputs */
  @Output() dayClick = new EventEmitter<DatePickerDayData>();
  @Output() dayHover = new EventEmitter<DatePickerDayData>();
  @Output() dayFocus = new EventEmitter<DatePickerDayData>();

  /** Computed properties */
  get isClickable(): boolean {
    return !this.disabled && !this.readonly && !this.dayData?.isDisabled;
  }

  get ariaLabel(): string {
    if (!this.dayData) return '';
    
    const date = this.dayData.date;
    const dateString = date.toLocaleDateString(this.locale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    let label = dateString;
    
    if (this.dayData.isToday) {
      label += ', Today';
    }
    
    if (this.dayData.isSelected) {
      label += ', Selected';
    }
    
    if (this.dayData.isDisabled) {
      label += ', Unavailable';
    }
    
    return label;
  }

  get displayNumber(): string {
    return this.dayData?.dayNumber?.toString() || '';
  }

  /** Event handlers */
  onDayClick(): void {
    if (this.isClickable && this.dayData) {
      this.dayClick.emit(this.dayData);
    }
  }

  onDayHover(): void {
    if (this.dayData) {
      this.dayHover.emit(this.dayData);
    }
  }

  onDayFocus(): void {
    if (this.dayData) {
      this.dayFocus.emit(this.dayData);
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onDayClick();
    }
  }
}
